import React, { FC } from 'react';
import { AccordionCareCard } from './AccordionCareCard';
import CareCard from './CareCard';
import { Button } from '../../jsxElements';
import { AmplitudeEvent, ClinicInfo } from '../../types';
import formatAddressLine from '../../utils/formatAddress';
import { formatGoogleMapsLocationLink } from '../../utils/map';
import styles from './ClinicDetailCard.module.css';
import sharedStyles from '../../shared.module.css';

import { AmpFlagSet } from '../../amplitude';

export const ClinicDetailDropdownCard: FC<{
    clinicInfo: ClinicInfo;
    expanded: boolean;
    featureFlags: AmpFlagSet;
    virtualWait: string | undefined;
}> = ({ clinicInfo, expanded, featureFlags, virtualWait }) => {
    let buttonTitle = '';
    let amplitudeEvent = {};

    if (clinicInfo.clinicType === 'UrgentCare') {
        buttonTitle = 'Get in line';
        amplitudeEvent = {
            eventType: 'Care Options - UC - Get in line ',
            eventProperties: {
        clinic_name: clinicInfo.displayName, // eslint-disable-line
        clinic_distance: clinicInfo.distance, // eslint-disable-line
        clinic_waittime: clinicInfo.waitTime, // eslint-disable-line
            },
        };
    } else if (clinicInfo.clinicType === 'Virtual') {
        buttonTitle = 'Start a virtual visit';
        amplitudeEvent = {
            eventType: 'Care Options - ECV - Link Click',
            eventProperties: {
        ecv_wait_time: clinicInfo.waitTime, // eslint-disable-line
        linkout_url: clinicInfo.url, // eslint-disable-line
            },
        };
    } else {
        buttonTitle = 'Schedule an appointment';
        amplitudeEvent = {
            eventType: 'Care Options - ECR - Schedule an appointment',
            eventProperties: {
        clinic_name: clinicInfo.displayName, // eslint-disable-line
        clinic_distance: clinicInfo.distance, // eslint-disable-line
            },
        };
    }

    const buttonInfo = {
        amplitudeEvent: amplitudeEvent as AmplitudeEvent,
        title: buttonTitle,
        url: clinicInfo.url,
    };

    const createFormattedDistanceWait = () => {
        let distance = '';
        let waitTime = '';
        if (clinicInfo.clinicType !== 'Virtual' && clinicInfo.distance) {
            distance = `${
                Math.round(clinicInfo.distance * 10) / 10
            } miles away`;
        } else {
            distance = 'Online Consultation';
        }
        if (
            clinicInfo.clinicType !== 'Virtual' &&
            clinicInfo.waitTime &&
            clinicInfo.waitTime !== 'N/A'
        ) {
            waitTime = clinicInfo.waitTime;
        } else if (
            clinicInfo.clinicType === 'Virtual' &&
            virtualWait &&
            virtualWait !== 'closed'
        ) {
            waitTime = virtualWait;
        }

        const estimatedWait =
            clinicInfo.clinicType === 'Virtual'
                ? `${waitTime} wait`
                : `${waitTime} minute wait`;

        return (
            <span>
                <p className={styles.clinicDetailDistanceTime}>
                    {' '}
                    {distance}
                    {waitTime ? <span>, {estimatedWait}</span> : null}
                </p>
            </span>
        );
    };

    const summaryContent = (
        <CareCard
            accordion
            address={
                clinicInfo.address
                    ? formatGoogleMapsLocationLink(
                          formatAddressLine(clinicInfo.address)
                      )
                    : ''
            }
            content={createFormattedDistanceWait()}
            expandText={
                <div className={styles.clinicDetailExpandText}>See details</div>
            }
            featureFlags={featureFlags}
            locationIcon={clinicInfo.clinicType !== 'Virtual'}
            serviceIcon={{
                virtual: clinicInfo.clinicType === 'Virtual',
                office: clinicInfo.clinicType !== 'Virtual',
            }}
            title={clinicInfo.displayName}
        >
            {clinicInfo.address && (
                <Button
                    button={buttonInfo}
                    className={sharedStyles.careCardButton}
                    disableButtons={false}
                    featureFlags={featureFlags}
                />
            )}
        </CareCard>
    );

    return (
        <div
            className={styles.clinicDetail}
            aria-label={`${clinicInfo.displayName}`}
        >
            <AccordionCareCard
                clinicInfo={clinicInfo}
                expanded={expanded}
                summaryContent={summaryContent}
            />
        </div>
    );
};
